import { COMMISSION_PLAN_LABEL } from '@shared/fees'
import { BROKER_LABEL, BROKER_NOTE } from '@shared/brokers'
import { useEffect, useState } from 'react'
import { Check, Pencil, Send, X, XCircle } from 'lucide-react'
import type { TicketRecord, TicketStatus } from '@shared/orders'
import { ordersApi } from '../../lib/api'
import { useOrder } from '../../hooks/useOrder'
import { useStore } from '../../store'
import { fmtPrice } from '../../lib/format'
import { cn } from '../../lib/utils'
import { Metric, Section, usd } from './WorkerCard'

/**
 * A chat order ("ticket") as a card. Every number comes from the server's compiled plan — the AI
 * never writes any of it. Nothing reaches IBKR before the two-step Approve.
 */

export const TICKET_STATUS_LABEL: Record<TicketStatus, string> = {
  draft: 'Awaiting approval',
  rejected: 'Rejected',
  expired: 'Expired',
  working: 'Working at the broker',
  filled: 'Filled',
  partial: 'Partly filled',
  cancelled: 'Cancelled',
  error: 'Rejected by the broker',
}

export function TicketPill({ status }: { status: TicketStatus }) {
  const tone = status === 'working' || status === 'filled' ? 'live' : status === 'draft' ? 'wait' : status === 'error' ? 'bad' : 'off'
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--color-inset)] px-2 py-0.5 text-[10.5px] font-medium',
        tone === 'bad' ? 'text-down' : tone === 'off' ? 'text-fg-faint' : 'text-fg-muted',
      )}
    >
      <span
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          tone === 'live' ? 'bg-up' : tone === 'wait' ? 'animate-pulse bg-fg-muted' : tone === 'bad' ? 'bg-down' : 'bg-flat',
        )}
      />
      {TICKET_STATUS_LABEL[status]}
    </span>
  )
}

const btn = 'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-medium transition-colors disabled:opacity-40'
const KEY_LABEL = { O: 'Order', 'O.TP': 'Target', 'O.SL': 'Stop' } as const

function OrderBody({ t }: { t: TicketRecord }) {
  const B = BROKER_LABEL[t.spec.broker ?? 'ibkr']
  const s = t.summary
  const p = t.plan
  const draft = t.status === 'draft'
  const sent = t.orders.length > 0 // a rejected or expired draft never reached IBKR
  const buy = t.spec.action === 'BUY'
  const main = t.orders.find((o) => o.key === 'O')
  const mainFills = t.fills.filter((f) => f.key === 'O')
  const filled = mainFills.reduce((a, f) => a + f.shares, 0) || main?.filled || 0
  const avg = mainFills.length ? mainFills.reduce((a, f) => a + f.shares * f.price, 0) / filled : main?.avgFillPrice ?? null
  const commission = t.fills.reduce((a, f) => a + (f.commission ?? 0), 0)
  const stop = p.orders.find((o) => o.key === 'O.SL')?.stopPrice
  const target = p.orders.find((o) => o.key === 'O.TP')?.limitPrice
  const failing = t.checks.filter((c) => !c.ok && c.level !== 'info')
  const signed = (n: number) => (n > 0 ? `+${n}` : String(n))
  const ended = t.endedAt != null
  // What actually happened to the position — the plan's "after" only while the order can still fill.
  const positionNow = p.positionBefore + (buy ? filled : -filled)
  const t0 = t.spec.orderType
  const priceLabel = p.refSource === 'quote' ? 'Last price' : t0 === 'STP' ? 'Stop price' : t0 === 'MIT' ? 'Trigger' : 'Limit price'
  return (
    <>
      <div className="grid grid-cols-2 gap-1.5 px-3.5 pb-2.5 sm:grid-cols-4">
        {!sent ? (
          <>
            <Metric label="Order value" value={usd(s.notional)} />
            <Metric label={priceLabel} value={fmtPrice(p.refPrice)} />
            <Metric label="Position" value={`${signed(p.positionBefore)} → ${signed(p.positionAfter)}`} />
            <Metric
              label={s.maxLossNet != null ? 'Max loss · after fees' : 'Max loss'}
              value={s.maxLoss == null ? 'No stop' : usd(-(s.maxLossNet ?? s.maxLoss))}
              tone={s.maxLoss != null ? 'down' : undefined}
              sub={s.maxLoss != null && s.maxLossNet != null ? `${usd(-s.maxLoss)} + fees` : undefined}
            />
          </>
        ) : (
          <>
            <Metric label="Filled" value={`${filled} / ${p.quantity}`} tone={filled >= p.quantity ? 'up' : undefined} />
            <Metric label="Avg price" value={avg != null ? fmtPrice(avg) : '—'} />
            <Metric label="Commission" value={commission ? usd(commission) : '—'} />
            <Metric label={ended ? 'Position' : 'Position on fill'} value={`${signed(p.positionBefore)} → ${signed(ended ? positionNow : p.positionAfter)}`} />
          </>
        )}
      </div>
      {!ended && (
        <div className="px-3.5 pb-2.5 text-[12px] text-fg-muted">
          {s.positionText}
          <span className="text-fg-faint"> · {s.quantityText}</span>
          {!sent && s.fees && (
            <span className="num block text-[11px] text-fg-faint">
              Est. fees ({COMMISSION_PLAN_LABEL[s.fees.plan]}): {usd(s.fees.order)} on fill
              {s.fees.exitAtStop != null || s.fees.exitAtTarget != null
                ? ` · ${usd(Math.max(s.fees.exitAtStop ?? 0, s.fees.exitAtTarget ?? 0))} when the ${s.fees.exitAtStop != null && s.fees.exitAtTarget != null ? 'stop or target' : s.fees.exitAtStop != null ? 'stop' : 'target'} fills`
                : ''}
            </span>
          )}
        </div>
      )}

      {(stop != null || target != null) && (
        <Section title="Protection">
          <div className="num text-[12px] text-fg-muted">
            {stop != null && <span>Stop {fmtPrice(stop)}</span>}
            {stop != null && target != null && <span className="text-fg-faint"> · </span>}
            {target != null && <span>Target {fmtPrice(target)}</span>}
            {!ended && (
              <span className="text-fg-faint">
                {' '}
                — held at {B}, live once the {buy ? 'buy' : 'sell'} fills{stop != null && target != null ? '; when one fills the other is cancelled' : ''}.
              </span>
            )}
          </div>
        </Section>
      )}

      {draft && t.checks.length > 0 && (
        <Section title="Checks" right={failing.length ? <span className="text-[10.5px] text-down">{failing.length} blocking</span> : null}>
          <ul className="space-y-0.5 text-[12px]">
            {t.checks.map((c) => (
              <li key={c.id} className="flex items-start gap-1.5">
                {c.level === 'info' ? (
                  <span className="mx-[3px] mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--line)]" />
                ) : c.ok ? (
                  <Check size={12} className="mt-[3px] shrink-0 text-up" />
                ) : (
                  <X size={12} className="mt-[3px] shrink-0 text-down" />
                )}
                <span className="line-clamp-2 min-w-0" title={`${c.label} — ${c.detail}`}>
                  <span className={c.ok ? 'text-fg-muted' : 'text-down'}>{c.label}</span>
                  <span className="text-fg-faint"> — {c.detail}</span>
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {draft && s.warnings.length > 0 && (
        <Section title="Keep in mind">
          <ul className="list-disc space-y-0.5 pl-4 text-[12px] text-fg-muted">
            {s.warnings.map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </Section>
      )}

      <Section title={sent ? `At ${B}` : `${draft ? `Exact ${B}` : `Planned — never sent to ${B}:`} order${p.orders.length > 1 ? `s · ${p.orders.length}` : ''}`}>
        {!sent ? (
          <pre className="overflow-x-auto whitespace-pre rounded-lg bg-[var(--color-inset)] p-2 text-[11px] leading-relaxed text-fg-muted">{s.ordersText.join('\n')}</pre>
        ) : (
          <table className="w-full text-[11.5px]">
            <tbody className="num">
              {t.orders.map((o) => (
                <tr key={o.orderId} className="text-fg-muted">
                  <td className="py-0.5 pr-2">{KEY_LABEL[o.key]}</td>
                  <td className="pr-2">{o.status}</td>
                  <td className="pr-2">{o.filled ? `${o.filled} @ ${fmtPrice(o.avgFillPrice)}` : ''}</td>
                  <td className="pr-2 text-down">{o.lastError ?? ''}</td>
                  <td className="text-right text-fg-faint">#{o.orderId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {t.error && <div className={cn('px-3.5 pb-2 text-[12px]', t.status === 'error' ? 'text-down' : 'text-fg-subtle')}>{t.error}</div>}
      {t.spec.note && (
        <Section title="Why (from the chat)">
          <p className="text-[11.5px] leading-relaxed text-fg-subtle">{t.spec.note}</p>
        </Section>
      )}
    </>
  )
}

export function OrderCard({ id, variant = 'chat' }: { id: string; variant?: 'chat' | 'page' }) {
  const { ticket: t, error, reload } = useOrder(id)
  const prefillComposer = useStore((s) => s.prefillComposer)
  const [armed, setArmed] = useState<null | 'approve' | 'cancel'>(null)
  const [busy, setBusy] = useState<null | 'approve' | 'reject' | 'cancel'>(null)
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null)

  useEffect(() => {
    if (!armed) return
    const x = setTimeout(() => setArmed(null), 6_000) // a confirmation never lingers
    return () => clearTimeout(x)
  }, [armed])
  useEffect(() => {
    if (msg?.tone !== 'ok') return
    const x = setTimeout(() => setMsg(null), 5_000) // the card itself shows the new state
    return () => clearTimeout(x)
  }, [msg])

  if (error && !t) return <div className="card mt-2 px-3.5 py-3 text-[12.5px] text-fg-subtle">Order {id}: {error}</div>
  if (!t) return <div className="card mt-2 h-24 animate-pulse" />

  const act = async (kind: NonNullable<typeof busy>, fn: () => Promise<unknown>, ok: string) => {
    setBusy(kind)
    setMsg(null)
    try {
      await fn()
      setMsg({ tone: 'ok', text: ok })
    } catch (e) {
      setMsg({ tone: 'err', text: e instanceof Error ? e.message : 'Something went wrong' })
    } finally {
      setBusy(null)
      setArmed(null)
      void reload()
    }
  }
  const B = BROKER_LABEL[t.spec.broker ?? 'ibkr']
  const draft = t.status === 'draft'
  const blocking = t.checks.some((c) => !c.ok && c.level !== 'info')
  const paper = /^DU/i.test(t.account ?? '')
  const active = t.orders.filter((o) => !['Filled', 'Cancelled', 'ApiCancelled', 'Inactive'].includes(o.status) && !o.cancelRequested)
  const mainActive = active.some((o) => o.key === 'O')
  const stopActive = active.some((o) => o.key === 'O.SL')
  // Once the order filled, what is left working is its protection — cancelling it leaves the position open.
  const cancelLabel = mainActive ? 'Cancel order' : stopActive ? (active.length > 1 ? 'Cancel stop & target' : 'Cancel stop') : 'Cancel target'
  const cancelConfirm = mainActive ? `Confirm — cancel at ${B}` : stopActive ? 'Confirm — the position stays open without a stop' : 'Confirm — cancel the target'

  return (
    <div className={cn('card overflow-hidden', variant === 'chat' && 'mt-2')}>
      <div className="flex items-start justify-between gap-3 px-3.5 pb-2 pt-3">
        <div className="min-w-0">
          <div className="text-[10.5px] font-semibold uppercase tracking-wide text-fg-faint">Order · {B}{paper ? ' · paper' : (t.spec.broker ?? 'ibkr') === 'robinhood' ? <span className="text-down"> · real money</span> : ''}</div>
          <div className="num mt-0.5 text-[14px] font-semibold text-fg">{t.summary.headline}</div>
        </div>
        <TicketPill status={t.status} />
      </div>

      <OrderBody t={t} />

      {(draft || active.length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-[var(--separator)] px-3.5 py-2.5">
          {draft && (
            <>
              <button
                type="button"
                disabled={!!busy || blocking}
                title={blocking ? 'A check is blocking this order' : undefined}
                onClick={() =>
                  armed === 'approve' ? void act('approve', () => ordersApi.approve(t.id, t.planHash), `Sent to ${B}.`) : setArmed('approve')
                }
                className={cn(btn, 'bg-[var(--color-accent)] text-[var(--color-base)]')}
              >
                {armed === 'approve' ? <Send size={13} /> : <Check size={13} />}
                {busy === 'approve' ? 'Sending…' : armed === 'approve' ? `Confirm — send ${t.spec.action === 'BUY' ? 'buy' : 'sell'} to ${B}` : 'Approve'}
              </button>
              <button
                type="button"
                disabled={!!busy}
                onClick={() => prefillComposer(`Emir taslağını düzenle (${t.id}): `)}
                className={cn(btn, 'border border-[var(--line-soft)] text-fg-muted hover:text-fg')}
              >
                <Pencil size={13} /> Edit
              </button>
              <button
                type="button"
                disabled={!!busy}
                onClick={() => void act('reject', () => ordersApi.reject(t.id), `Rejected — nothing was sent to ${B}.`)}
                className={cn(btn, 'text-fg-subtle hover:text-down')}
              >
                <X size={13} /> Reject
              </button>
              <span className="num ml-auto text-[10.5px] text-fg-faint" title="Approval is bound to exactly this order">
                order #{t.planHash.slice(0, 8)}
              </span>
            </>
          )}
          {!draft && active.length > 0 && (
            <button
              type="button"
              disabled={!!busy}
              onClick={() =>
                armed === 'cancel'
                  ? void act('cancel', async () => {
                      const r = await ordersApi.cancel(t.id)
                      if (!r.ok) throw new Error(r.error ?? 'Could not cancel')
                    }, `Cancel sent to ${B}.`)
                  : setArmed('cancel')
              }
              className={cn(btn, armed === 'cancel' ? 'bg-down text-white' : 'border border-[var(--line-soft)] text-fg-muted hover:text-down')}
            >
              <XCircle size={13} />
              {armed === 'cancel' ? cancelConfirm : cancelLabel}
            </button>
          )}
        </div>
      )}
      {msg && <div className={cn('px-3.5 pb-2.5 text-[12px]', msg.tone === 'ok' ? 'text-up' : 'text-down')}>{msg.text}</div>}
    </div>
  )
}
